import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaQuestionCircle, FaCode } from 'react-icons/fa';

let nextDraftItemId = 0;

const createDraftId = (prefix) => {
    nextDraftItemId += 1;
    return `${prefix}-${nextDraftItemId}`;
};

const createOption = () => ({
    clientId: createDraftId('option'),
    option_text: '',
    is_correct: false,
});

const createTestCase = () => ({
    clientId: createDraftId('test-case'),
    input_data: '',
    expected_output: '',
});

const stripClientId = ({ clientId, ...item }) => item;

const SingleQuesModal = ({ onClose, onSubmit, categoryId }) => {
    const [questionType, setQuestionType] = useState(null); // State to track question type
    const [questionText, setQuestionText] = useState('');
    const [points, setPoints] = useState(10); // Default points
    const [options, setOptions] = useState([createOption()]); // Initial option
    const [programmingDetails, setProgrammingDetails] = useState({
        programming_language: '',
        input_format: '',
        output_format: '',
        sample_input: '',
        sample_output: '',
        time_limit: '',
        memory_limit: '',
        test_cases: [createTestCase()],
    });

    const handleOptionChange = (optionId, field, value) => {
        setOptions((currentOptions) => currentOptions.map((option) => (
            option.clientId === optionId
                ? { ...option, [field]: value }
                : option
        )));
    };

    const handleAddOption = () => {
        setOptions((currentOptions) => [...currentOptions, createOption()]);
    };

    const handleRemoveOption = (optionId) => {
        setOptions((currentOptions) => currentOptions.filter(
            (option) => option.clientId !== optionId,
        ));
    };

    const handleProgrammingDetailChange = (field, value) => {
        setProgrammingDetails({ ...programmingDetails, [field]: value });
    };

    const handleTestCaseChange = (testCaseId, field, value) => {
        setProgrammingDetails((currentDetails) => ({
            ...currentDetails,
            test_cases: currentDetails.test_cases.map((testCase) => (
                testCase.clientId === testCaseId
                    ? { ...testCase, [field]: value }
                    : testCase
            )),
        }));
    };

    const handleAddTestCase = () => {
        setProgrammingDetails((currentDetails) => ({
            ...currentDetails,
            test_cases: [...currentDetails.test_cases, createTestCase()],
        }));
    };

    const handleRemoveTestCase = (testCaseId) => {
        setProgrammingDetails((currentDetails) => ({
            ...currentDetails,
            test_cases: currentDetails.test_cases.filter(
                (testCase) => testCase.clientId !== testCaseId,
            ),
        }));
    };

    const handleSubmit = () => {
        const questionData = {
            category_id: categoryId,
            question_text: questionText,
            points: points,
            question_type: questionType,
            ...(questionType === 'MCQ' && {
                options: options.map(stripClientId),
            }),
            ...(questionType === 'Programming' && {
                programming_details: {
                    ...programmingDetails,
                    test_cases: programmingDetails.test_cases.map(stripClientId),
                },
            }),
        };
        onSubmit(questionData);
        onClose();
    };

    const renderMCQForm = () => (
        <>
            {/* Question Text */}
            <div className="mb-4">
                <label htmlFor="mcq-question-text" className="block text-sm font-medium text-campus-text-primary mb-1">Question Text</label>
                <textarea
                    id="mcq-question-text"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    rows="3"
                    className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                    placeholder="Enter your question here..."
                />
            </div>

            {/* Points */}
            <div className="mb-4">
                <label htmlFor="mcq-points" className="block text-sm font-medium text-campus-text-primary mb-1">Points</label>
                <input
                    id="mcq-points"
                    type="number"
                    value={points}
                    onChange={(e) => setPoints(e.target.value)}
                    className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                    placeholder="Enter points for the question"
                />
            </div>

            {/* Options */}
            <div className="mb-4">
                <fieldset className="p-4 bg-campus-bg rounded-xl border border-campus-border">
                    <legend className="block text-sm font-semibold text-campus-text-primary mb-3">Options</legend>
                    <div className="space-y-3">
                        {options.map((option, index) => (
                            <div key={option.clientId} className="flex items-center gap-3">
                                <label htmlFor={`mcq-option-${option.clientId}`} className="sr-only">Option {index + 1}</label>
                                <input
                                    id={`mcq-option-${option.clientId}`}
                                    type="text"
                                    value={option.option_text}
                                    onChange={(e) => handleOptionChange(option.clientId, 'option_text', e.target.value)}
                                    className="flex-1 p-3 rounded-lg border border-campus-border bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                                    placeholder={`Option ${index + 1}`}
                                />
                                <label htmlFor={`mcq-correct-${option.clientId}`} className="flex items-center cursor-pointer min-w-24">
                                    <input
                                        id={`mcq-correct-${option.clientId}`}
                                        type="checkbox"
                                        checked={option.is_correct}
                                        onChange={(e) => handleOptionChange(option.clientId, 'is_correct', e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-campus-primary rounded border-campus-border focus:ring-campus-primary"
                                    />
                                    <span className="ml-2 font-medium text-campus-text-body">Correct</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveOption(option.clientId)}
                                    className="p-2 text-campus-text-body hover:text-campus-error hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={handleAddOption}
                        className="mt-4 px-4 py-2 border border-campus-border text-campus-text-primary rounded-lg hover:bg-campus-surface font-medium transition-colors shadow-sm bg-white"
                    >
                        + Add Option
                    </button>
                </fieldset>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-campus-border">
                <button type="button" className="px-5 py-2 border border-campus-border text-campus-text-body rounded-lg hover:bg-campus-bg font-medium transition-colors" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className="px-6 py-2 bg-campus-primary text-white rounded-lg hover:bg-campus-primary-dark font-medium transition-colors shadow-sm" onClick={handleSubmit}>
                    Submit
                </button>
            </div>
        </>
    );

    const renderProgrammingForm = () => (
        <>
            <div className="mb-4">
                <label htmlFor="prog-question-text" className="block text-sm font-medium text-campus-text-primary mb-1">Question Text</label>
                <textarea
                    id="prog-question-text"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    rows="3"
                    className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                    placeholder="Enter your question here..."
                />
            </div>

            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="prog-points" className="block text-sm font-medium text-campus-text-primary mb-1">Points</label>
                    <input
                        id="prog-points"
                        type="number"
                        value={points}
                        onChange={(e) => setPoints(e.target.value)}
                        className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                        placeholder="Points"
                    />
                </div>
                <div>
                    <label htmlFor="prog-language" className="block text-sm font-medium text-campus-text-primary mb-1">Programming Language</label>
                    <input
                        id="prog-language"
                        type="text"
                        value={programmingDetails.programming_language}
                        onChange={(e) => handleProgrammingDetailChange('programming_language', e.target.value)}
                        className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                        placeholder="Language"
                    />
                </div>
                <div>
                    <label htmlFor="prog-time-limit" className="block text-sm font-medium text-campus-text-primary mb-1">Time Limit (ms)</label>
                    <input
                        id="prog-time-limit"
                        type="number"
                        value={programmingDetails.time_limit}
                        onChange={(e) => handleProgrammingDetailChange('time_limit', e.target.value)}
                        className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                        placeholder="Time Limit"
                    />
                </div>
                <div>
                    <label htmlFor="prog-memory-limit" className="block text-sm font-medium text-campus-text-primary mb-1">Memory Limit (MB)</label>
                    <input
                        id="prog-memory-limit"
                        type="number"
                        value={programmingDetails.memory_limit}
                        onChange={(e) => handleProgrammingDetailChange('memory_limit', e.target.value)}
                        className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                        placeholder="Memory Limit"
                    />
                </div>
            </div>

            {/* Programming Details */}
            <div className="mb-4">
                <label htmlFor="prog-input-format" className="block text-sm font-medium text-campus-text-primary mb-1">Input Format</label>
                <textarea
                    id="prog-input-format"
                    rows="2"
                    value={programmingDetails.input_format}
                    onChange={(e) => handleProgrammingDetailChange('input_format', e.target.value)}
                    className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                    placeholder="Enter input format"
                />
            </div>

            <div className="mb-4">
                <label htmlFor="prog-output-format" className="block text-sm font-medium text-campus-text-primary mb-1">Output Format</label>
                <textarea
                    id="prog-output-format"
                    rows="2"
                    value={programmingDetails.output_format}
                    onChange={(e) => handleProgrammingDetailChange('output_format', e.target.value)}
                    className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                    placeholder="Enter output format"
                />
            </div>

            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="prog-sample-input" className="block text-sm font-medium text-campus-text-primary mb-1">Sample Input</label>
                    <textarea
                        id="prog-sample-input"
                        rows="2"
                        value={programmingDetails.sample_input}
                        onChange={(e) => handleProgrammingDetailChange('sample_input', e.target.value)}
                        className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary font-mono text-sm focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                        placeholder="Sample input"
                    />
                </div>
                <div>
                    <label htmlFor="prog-sample-output" className="block text-sm font-medium text-campus-text-primary mb-1">Sample Output</label>
                    <textarea
                        id="prog-sample-output"
                        rows="2"
                        value={programmingDetails.sample_output}
                        onChange={(e) => handleProgrammingDetailChange('sample_output', e.target.value)}
                        className="w-full p-3 rounded-lg border border-campus-border bg-campus-bg text-campus-text-primary font-mono text-sm focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                        placeholder="Sample output"
                    />
                </div>
            </div>

            {/* Test Cases */}
            <div className="mb-4">
                <fieldset className="p-4 bg-campus-bg rounded-xl border border-campus-border">
                    <legend className="block text-sm font-semibold text-campus-text-primary mb-3">Test Cases</legend>
                    <div className="space-y-3">
                        {programmingDetails.test_cases.map((testCase, index) => (
                            <div key={testCase.clientId} className="flex gap-2 items-start relative bg-campus-surface p-3 rounded-lg border border-campus-border">
                                <div className="flex-1 space-y-2">
                                    <label htmlFor={`tc-input-${testCase.clientId}`} className="sr-only">Test case {index + 1} input data</label>
                                    <textarea
                                        id={`tc-input-${testCase.clientId}`}
                                        rows="2"
                                        value={testCase.input_data}
                                        onChange={(e) => handleTestCaseChange(testCase.clientId, 'input_data', e.target.value)}
                                        className="w-full p-2 rounded-md border border-campus-border bg-campus-bg text-campus-text-primary font-mono text-sm focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                                        placeholder="Input Data"
                                    />
                                    <label htmlFor={`tc-output-${testCase.clientId}`} className="sr-only">Test case {index + 1} expected output</label>
                                    <textarea
                                        id={`tc-output-${testCase.clientId}`}
                                        rows="2"
                                        value={testCase.expected_output}
                                        onChange={(e) => handleTestCaseChange(testCase.clientId, 'expected_output', e.target.value)}
                                        className="w-full p-2 rounded-md border border-campus-border bg-campus-bg text-campus-text-primary font-mono text-sm focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all"
                                        placeholder="Expected Output"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveTestCase(testCase.clientId)}
                                    className="p-2 mt-1 text-campus-text-body hover:text-campus-error hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={handleAddTestCase}
                        className="mt-4 px-4 py-2 border border-campus-border text-campus-text-primary rounded-lg hover:bg-campus-surface font-medium transition-colors shadow-sm bg-white"
                    >
                        + Add Test Case
                    </button>
                </fieldset>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-campus-border">
                <button type="button" className="px-5 py-2 border border-campus-border text-campus-text-body rounded-lg hover:bg-campus-bg font-medium transition-colors" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className="px-6 py-2 bg-campus-primary text-white rounded-lg hover:bg-campus-primary-dark font-medium transition-colors shadow-sm" onClick={handleSubmit}>
                    Submit
                </button>
            </div>
        </>
    );

    let content;
    if (!questionType) {
        content = (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                    type="button"
                    className="flex flex-col items-center p-6 bg-campus-surface border border-campus-border rounded-2xl shadow-sm cursor-pointer hover:shadow-hover hover:border-campus-primary transition-all duration-200"
                    onClick={() => setQuestionType('MCQ')}
                >
                    <div className="p-3 bg-red-50 rounded-full mb-4">
                        <FaQuestionCircle size={32} className="text-campus-primary" />
                    </div>
                    <h4 className="text-lg font-heading font-bold text-campus-text-primary text-center mb-2">Multiple Choice Question (MCQ)</h4>
                    <p className="text-sm text-campus-text-body text-center leading-relaxed">Choose this for questions with multiple predefined options.</p>
                </button>
                <button
                    type="button"
                    className="flex flex-col items-center p-6 bg-campus-surface border border-campus-border rounded-2xl shadow-sm cursor-pointer hover:shadow-hover hover:border-campus-primary transition-all duration-200"
                    onClick={() => setQuestionType('Programming')}
                >
                    <div className="p-3 bg-red-50 rounded-full mb-4">
                        <FaCode size={32} className="text-campus-primary" />
                    </div>
                    <h4 className="text-lg font-heading font-bold text-campus-text-primary text-center mb-2">Programming Question</h4>
                    <p className="text-sm text-campus-text-body text-center leading-relaxed">Choose this for coding-related tasks, providing test inputs/outputs.</p>
                </button>
            </div>
        );
    } else if (questionType === 'MCQ') {
        content = renderMCQForm();
    } else {
        content = renderProgrammingForm();
    }

    return (
        <div className="p-4 sm:p-6 max-h-[85vh] overflow-y-auto w-full max-w-4xl mx-auto custom-scrollbar">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-campus-border">
                <h3 className="text-2xl font-heading font-bold text-campus-text-primary">{questionType ? `Create ${questionType} Question` : 'Select Question Type'}</h3>
            </div>
            {content}
        </div>
    );
};

SingleQuesModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default SingleQuesModal;
